import React, { useEffect, useState } from "react";
import { Settings, Trash2, AlertTriangle, FileText } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";

import { OptionCard } from "@/components/onboarding/OptionCard";
import { slugify } from "@/utils/slug";

type PositPrivacy = Database["public"]["Enums"]["posit_privacy"];
type PositResponseType = Database["public"]["Enums"]["posit_response_type"];

interface EditPositSettingsDialogProps {
  positId: string;
  currentTitle: string;
  currentPublicDescription?: string | null;
  currentPrivacy: PositPrivacy;
  currentIsMonetized: boolean;
  currentPrice: number | null;
  currentResponseType?: PositResponseType;
  onSettingsUpdated: () => void;
  onDeleted?: () => void;
  buttonClassName?: string;
  buttonSize?: "sm" | "default" | "lg";
  showLabel?: boolean;
  iconSize?: "sm" | "default";
}

const privacyOptions = [
  { 
    value: "public", 
    label: "Public", 
    description: "Discoverable by anyone"
  },
  { 
    value: "private", 
    label: "Private", 
    description: "Only you can access"
  },
  { 
    value: "exclusive", 
    label: "Exclusive", 
    description: "Share with specific people"
  }
];

const monetizationOptions = [
  { 
    value: "free", 
    label: "Free", 
    description: "Free access for everyone"
  },
  { 
    value: "paid", 
    label: "Paid", 
    description: "Charge for access"
  }
];

// Response style selection removed: Posits always respond in first person

const EditPositSettingsDialog = ({
  positId,
  currentTitle,
  currentPublicDescription,
  currentPrivacy,
  currentIsMonetized,
  currentPrice,
  currentResponseType = "you",
  onSettingsUpdated,
  onDeleted,
  buttonClassName = "h-10",
  buttonSize,
  showLabel = true,
  iconSize = "default"
}: EditPositSettingsDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState(currentTitle);
  const [publicDescription, setPublicDescription] = useState(currentPublicDescription || "");
  const [privacy, setPrivacy] = useState<PositPrivacy>(currentPrivacy);
  const [monetization, setMonetization] = useState(currentIsMonetized ? "paid" : "free");
  const [price, setPrice] = useState(currentPrice || 5);
  // Response type fixed to 'you' (first person)
  const responseType: PositResponseType = "you" as PositResponseType;
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Mirror Create flow: determine if user is premium (or trial) to gate private/exclusive
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (!user?.id) return;
      try {
        const { data: userData } = await supabase
          .from("users")
          .select("subscription_type, trial_end_date")
          .eq('id', user.id)
          .single();
        const isTrialValid = userData?.trial_end_date
          ? new Date(userData.trial_end_date) > new Date()
          : false;
        setIsPremium(userData?.subscription_type === 'posit_plus' || (userData?.subscription_type === 'trial' && isTrialValid));
      } catch (e) {
        setIsPremium(false);
      }
    };
    checkSubscriptionStatus();
  }, [user?.id]);

  const handleDelete = async () => {
    setIsDeleting(true);
    
    try {
      // Use secure edge function for complete deletion (includes vector cleanup)
      const { deletePositSecurely } = await import('../../utils/secureApiCalls');
      
      const result = await deletePositSecurely(positId);
      
      if (!result.success) {
        throw new Error(result.message);
      }

      toast({
        title: "Posit Deleted",
        description: "Your posit has been deleted successfully "
      });

      // Refresh the posits list
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: ["user-posits", user.id] });
      }

      // Call the onDeleted callback if provided
      if (onDeleted) {
        onDeleted();
      }
      
      // Close the settings dialog
      setIsOpen(false);
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete posit",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSave = async () => {
    try {
      // Validate title
      if (!title.trim()) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Please enter a title for your Posit"
        });
        return;
      }

      // Validate price for paid posits
      if (monetization === "paid" && (!price || price < 1)) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Please set a valid monthly price (minimum $1)"
        });
        return;
      }

              // Handle privacy change separately with secure edge function
        if (privacy !== currentPrivacy) {
        const { updatePositPrivacySecurely } = await import('../../utils/secureApiCalls');
        
        const privacyResult = await updatePositPrivacySecurely(positId, privacy);
        
        if (!privacyResult.success) {
          throw new Error(`Privacy update failed: ${privacyResult.message}`);
        }
      }

      // Update other settings via direct database call
      const { error } = await supabase
        .from("posits")
        .update({
          title: title.trim(),
          public_description: publicDescription.trim() || null,
          is_monetized: monetization === "paid",
          is_paid: monetization === "paid",
          subscription_price: monetization === "paid" ? price : null,
          response_type: "you",
          updated_by: "user"
        })
        .eq("id", positId);

      if (error) throw error;

      // After saving, ensure wiki Overview exists/syncs if publicDescription present
      try {
        const token = (await supabase.auth.getSession()).data.session?.access_token;
        if (token && publicDescription.trim()) {
          await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/save-wiki-document`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({
              posit_id: positId,
              content_md: `## Overview\n\n${publicDescription.trim()}\n`,
              update_lead: false,
              public_default: true,
            }),
          });
        }
      } catch (e) {
      }

      toast({
        title: "Settings updated",
        description: "Your Posit settings have been updated successfully."
      });
      onSettingsUpdated();
      setIsOpen(false);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update settings. Please try again."
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size={buttonSize ?? "sm"} className={buttonClassName}>
          <Settings className={iconSize === "sm" ? "w-3 h-3" : "h-4 w-4"} />
          {showLabel && <span className={iconSize === "sm" ? "" : "ml-2"}>Settings</span>}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl w-[calc(100%-2rem)] mx-auto">
        <DialogHeader className="space-y-2">
          <DialogTitle>Posit Settings</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2 max-h-[calc(100vh-10rem)] overflow-y-auto px-1">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="posit-title" className="text-base font-medium">
              Posit Title
            </Label>
            <Input
              id="posit-title"
              placeholder="e.g., Marketing Strategy Guide, Python Coding Help"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="text-lg h-12"
              maxLength={100}
            />
          </div>

          {/* Public Description */}
          <div className="space-y-2">
            <Label htmlFor="public-description" className="text-base font-medium">
              Description <span className="text-sm text-muted-foreground">(Optional)</span>
            </Label>
            <Textarea
              id="public-description"
              value={publicDescription}
              onChange={(e) => setPublicDescription(e.target.value)}
              className="resize-none min-h-[60px]"
              maxLength={80}
            />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <p>This will be shown on your Posit card in the marketplace.</p>
              <span>{publicDescription.length}/80</span>
            </div>
          </div>

          {/* Response Style removed – always first person ('you') */}

          {/* Privacy */}
          <div className="space-y-4">
            <Label className="text-base font-medium">Privacy</Label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {privacyOptions.map((option) => {
                const isPrivateOrExclusive = option.value === "private" || option.value === "exclusive";
                const shouldDisable = isPrivateOrExclusive && !isPremium;
                return (
                  <OptionCard
                    key={option.value}
                    option={option}
                    isSelected={privacy === option.value}
                    onSelect={(value) => setPrivacy(value as PositPrivacy)}
                    isHovered={hoveredCard === option.value}
                    onHover={setHoveredCard}
                    disabled={shouldDisable}
                    disabledReason={shouldDisable ? "Requires Posit Plus" : undefined}
                  />
                );
              })}
            </div>
          </div>

          {/* Monetization */}
          <div className="space-y-4">
            <Label className="text-base font-medium">Monetization</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {monetizationOptions.map((option) => (
                <OptionCard
                  key={option.value}
                  option={option}
                  isSelected={monetization === option.value}
                  onSelect={(value) => setMonetization(value)}
                  isHovered={hoveredCard === option.value}
                  onHover={setHoveredCard}
                />
              ))}
            </div>
            
            {monetization === "paid" && (
              <div className="space-y-2 mt-4 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <Label htmlFor="price" className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  Monthly subscription price (required for paid posits)
                </Label>
                <div className="flex items-center space-x-2">
                  <span className="text-lg">$</span>
                  <Input
                    id="price"
                    type="number"
                    min="1"
                    max="999"
                    value={price}
                    onChange={(e) => setPrice(parseInt(e.target.value) || 0)}
                    className="w-32"
                    placeholder="5"
                  />
                  <span className="text-sm text-muted-foreground">per month</span>
                </div>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Revenue split: 90% you, 10% platform
                </p>
              </div>
            )}
          </div>

          {/* Details Management */}
          <div className="space-y-2 pt-2">
            <Label className="text-base font-medium">Details</Label>
            <div>
              <Button asChild variant="outline" size="sm">
                <Link to={`/d/${positId}/${slugify(title)}`}>
                  <FileText className="w-4 h-4 mr-2" />
                  View Details
                </Link>
              </Button>
              {/* No edit from settings; editing happens in private editor */}
            </div>
          </div>



          <div className="flex justify-between pt-4">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="text-destructive hover:text-destructive">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Posit
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                    Delete Posit
                  </AlertDialogTitle>
                  <AlertDialogDescription className="text-left">
                    Are you sure you want to delete <span className="font-semibold">"{title}"</span>?
                    <br />
                    <br />
                    This action cannot be undone. This will permanently delete:
                    <ul className="mt-2 ml-4 text-sm list-disc space-y-1">
                      <li>The posit and all its content</li>
                      <li>All chat history and messages</li>
                      <li>Analytics and usage data</li>
                      <li>Subscriber data and reviews</li>
                      <li>Any shared access permissions</li>
                    </ul>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="bg-destructive hover:bg-destructive/90"
                  >
                    {isDeleting ? "Deleting..." : "Delete Forever"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave}>Save Changes</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
export default EditPositSettingsDialog;